import React, { useState } from 'react';
import { AlertTriangle, GitMerge, Search } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useClientes } from '../hooks';
import { executarMerge, previewMerge, type MergePreview, type ClienteListItem } from '../api';
import { usePermissions, FEATURES } from '../../../shared/hooks/usePermissions';
import { getFriendlyErrorMessage } from '../../../shared/lib/errors';
import { Textarea } from '../../../shared/components/Textarea';

/**
 * Tela admin de fusão de duplicidades (SPEC 02 §1 — RN-COM-057).
 * Restrita a perfis com `client:merge` (default: só admin).
 *
 * Fluxo em 3 passos:
 *  1. Busca dois clientes (por nome/CNPJ) e escolhe primary + secondary.
 *  2. Preview: mostra quantas linhas serão migradas e conflitos
 *     detectados (endereço/contato principal duplicado, etc.).
 *  3. Confirmação dupla: digita "CONFIRMO" antes de executar.
 */
const FusaoDuplicidadesPage: React.FC = () => {
  const { can } = usePermissions();
  const canMerge = can(FEATURES.clientesFusao, 'edit');
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const { data: searchData, isLoading: searchLoading } = useClientes(
    search.length >= 3 ? { search, limit: 20 } : undefined,
  );
  const items: ClienteListItem[] = searchData?.data ?? [];

  const [primary, setPrimary] = useState<ClienteListItem | null>(null);
  const [secondary, setSecondary] = useState<ClienteListItem | null>(null);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [reason, setReason] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!canMerge) {
    return (
      <div className="p-6">
        <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Acesso restrito. Você não tem permissão <code>client:merge</code>.
        </div>
      </div>
    );
  }

  const handlePreview = async () => {
    if (!primary || !secondary) return;
    setError(null);
    setLoadingPreview(true);
    try {
      const res = await previewMerge(primary.id, secondary.id);
      setPreview(res);
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao carregar preview.'));
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleExecute = async () => {
    if (!primary || !secondary || confirmText !== 'CONFIRMO') return;
    setError(null);
    setExecuting(true);
    try {
      await executarMerge(primary.id, secondary.id, reason.trim() || undefined);
      // Após fusão, navega para o primary — o secondary passa a apontar
      // para ele via `merged_into_id`.
      navigate(`/clientes/${primary.id}`, {
        state: { successMessage: 'Fusão executada com sucesso.' },
      });
    } catch (err) {
      setError(getFriendlyErrorMessage(err, 'Erro ao executar fusão.'));
    } finally {
      setExecuting(false);
    }
  };

  const reset = () => {
    setPrimary(null);
    setSecondary(null);
    setPreview(null);
    setReason('');
    setConfirmText('');
    setError(null);
  };

  return (
    <div className="p-6 space-y-4" data-testid="fusao-duplicidades">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GitMerge size={22} /> Fusão de duplicidades
        </h1>
        <p className="text-sm text-gray-500">
          Consolida dois cadastros do mesmo cliente. O secundário passa a apontar para o principal
          via <code>merged_into_id</code> e é marcado como Encerrado. Log imutável em{' '}
          <code>client_merge_log</code>. Operação restrita a admin (RN-COM-057).
        </p>
      </div>

      {/* Passo 1 — escolha dos clientes */}
      <section className="rounded-md border border-gray-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-gray-700">1. Selecione os cadastros</h2>
        <div className="mb-3 flex items-center gap-2">
          <Search size={14} className="text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nome ou CNPJ (mín 3 caracteres)…"
            className="flex-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="fusao-search"
          />
        </div>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <PickerCard
            label="Principal (permanece)"
            selected={primary}
            onClear={() => {
              setPrimary(null);
              setPreview(null);
            }}
          />
          <PickerCard
            label="Secundário (será unificado)"
            selected={secondary}
            onClear={() => {
              setSecondary(null);
              setPreview(null);
            }}
          />
        </div>
        {items.length > 0 && (
          <div className="mt-3 max-h-64 overflow-y-auto rounded border border-gray-200">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-2 py-1 text-left">Código</th>
                  <th className="px-2 py-1 text-left">Razão Social</th>
                  <th className="px-2 py-1 text-left">Documento</th>
                  <th className="px-2 py-1 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="px-2 py-1 font-mono text-xs">{c.code}</td>
                    <td className="px-2 py-1">{c.razaoSocial}</td>
                    <td className="px-2 py-1 font-mono text-xs">{c.documento}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        onClick={() => {
                          setPrimary(c);
                          setPreview(null);
                        }}
                        disabled={primary?.id === c.id || secondary?.id === c.id}
                        className="mr-2 rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-200 disabled:opacity-50"
                      >
                        Principal
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSecondary(c);
                          setPreview(null);
                        }}
                        disabled={primary?.id === c.id || secondary?.id === c.id}
                        className="rounded bg-red-100 px-2 py-0.5 text-xs text-red-700 hover:bg-red-200 disabled:opacity-50"
                      >
                        Secundário
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {searchLoading && <div className="mt-2 text-xs text-gray-500">Buscando…</div>}
      </section>

      {/* Passo 2 — preview */}
      {primary && secondary && (
        <section className="rounded-md border border-gray-200 bg-white p-4">
          <h2 className="mb-3 text-sm font-semibold text-gray-700">2. Preview da fusão</h2>
          {!preview ? (
            <button
              type="button"
              onClick={handlePreview}
              disabled={loadingPreview}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              data-testid="btn-preview"
            >
              {loadingPreview ? 'Carregando…' : 'Calcular preview'}
            </button>
          ) : (
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500">Vínculos que serão migrados para o principal:</div>
                <ul className="mt-1 text-sm">
                  {Object.entries(preview.fkCounts).map(([table, count]) => (
                    <li key={table} className="flex justify-between">
                      <span className="font-mono text-xs">{table}</span>
                      <span className="font-medium">{count}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {preview.conflicts.length > 0 && (
                <div
                  className="rounded border border-yellow-300 bg-yellow-50 p-2 text-xs text-yellow-800"
                  data-testid="preview-conflicts"
                >
                  <strong>Conflitos detectados:</strong>
                  <ul className="mt-1 list-inside list-disc">
                    {preview.conflicts.map((c) => (
                      <li key={c}>{c}</li>
                    ))}
                  </ul>
                  <p className="mt-1">
                    O secundário será rebaixado (não principal) antes da migração.
                  </p>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Passo 3 — confirmação dupla */}
      {preview && (
        <section className="rounded-md border border-red-200 bg-red-50 p-4">
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-red-900">
            <AlertTriangle size={16} /> 3. Confirmação dupla
          </h2>
          <div className="space-y-3 text-sm">
            <p className="text-red-900">
              Operação <strong>não reversível pela UI</strong>. O log guarda snapshot e mapa de
              FKs para reversão manual em incidente. Digite <code className="rounded bg-white px-1">CONFIRMO</code> para habilitar.
            </p>
            <Textarea
              label="Motivo (opcional)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              data-testid="fusao-motivo"
            />
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-red-900">Digite CONFIRMO</span>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                className="w-full rounded-md border border-red-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                data-testid="fusao-confirm"
              />
            </label>
            {error && (
              <div className="rounded border border-red-300 bg-white p-2 text-xs text-red-800">
                {error}
              </div>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleExecute}
                disabled={executing || confirmText !== 'CONFIRMO'}
                className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                data-testid="btn-executar-fusao"
              >
                {executing ? 'Executando…' : 'Executar fusão'}
              </button>
              <button
                type="button"
                onClick={reset}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
            </div>
          </div>
        </section>
      )}

      <div className="text-xs text-gray-500">
        <Link to="/clientes" className="underline">
          ← Voltar para lista de clientes
        </Link>
      </div>
    </div>
  );
};

interface PickerProps {
  label: string;
  selected: ClienteListItem | null;
  onClear: () => void;
}

const PickerCard: React.FC<PickerProps> = ({ label, selected, onClear }) => (
  <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
    <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
    {selected ? (
      <div className="mt-1 space-y-0.5">
        <div className="font-medium text-gray-900">{selected.razaoSocial}</div>
        <div className="text-xs text-gray-500 font-mono">
          {selected.code} · {selected.documento}
        </div>
        <button
          type="button"
          onClick={onClear}
          className="mt-1 text-xs text-blue-600 hover:underline"
        >
          Remover
        </button>
      </div>
    ) : (
      <div className="mt-1 text-xs text-gray-400">— nenhum selecionado —</div>
    )}
  </div>
);

export default FusaoDuplicidadesPage;
